import { track } from 'lib/analytics'
import { createHolidayWishlistPath } from 'lib/urls'
import { LandingPageSections } from '../LandingPage.types'

const holidayPageSections: LandingPageSections[] = [
  {
    type: 'hero',
    id: '1',
    desktopWidth: 'full',
    images: {
      desktop:
        'https://images.babylist.com/image/upload/q_50/v1701139489/Hero_Image_-_Desktop_-_12_4_2023_tc9esb.jpg',
      mobile:
        'https://images.babylist.com/image/upload/q_50/v1701139469/Hero_Image_-_Mobile_-_12_4_2023_aratie.jpg',
    },
    orientation: 'stacked',
    headerText: 'Last Chance for On-Time Gift Delivery',
    subHeaderText:
      'Find gear, clothing and heirloom-worthy keepsakes in the Babylist Shop. Order by Dec. 11 for on-time shipping.',
    callToAction: {
      type: 'link',
      id: '1-1',
      text: 'Shop Holiday Gifts',
      onClick: (currentUser, tracker) => {
        const path = '/store/holiday-shop'

        tracker.trackEvent({
          event: track.componentClicked,
          eventType: track.EventType.CLICK,
          eventLocation: track.EventLocation.HOLIDAY_LANDING_PAGE,
          eventUrl: window.location.href,
          componentType: track.ComponentType.HERO_BANNER as any,
          registryOwnerId: currentUser?.id,
          registryId: currentUser?.registryId,
          isRegistryOwner:
            currentUser?.currentRegistry?.ownerId === currentUser?.id,
          urlClicked: new URL(path, window.location.origin).toString(),
          positionInList: 1,
          slotPosition: 1,
        })

        window.location.href = path
      },
    },
  },
  {
    type: 'spacer',
    id: 'spacer_section_1',
  },
  {
    type: 'text',
    id: '2',
    desktopWidth: 'limited',
    heading: 'Gift Guides for Babies',
    background: 'none',
    includeVerticalSectionSpacing: false,
  },
  {
    type: 'guides',
    id: '3',
    heading: 'Gift Guides for Babies',
    slugs: [
      'best-gifts-baby-first-holiday',
      'best-baby-toys-first-6-months',
      'best-montessori-toys-babies',
      'best-toys-6-month-old-baby',
      'best-stocking-stuffers-babies',
      'best-toys-9-to-12-month-old-babies',
      'best-personalized-baby-gifts',
      'minimalist-holiday-gift-guide',
    ],
    includeVerticalSectionSpacing: true,
    columns: 4,
  },
  {
    type: 'text',
    id: '4',
    desktopWidth: 'limited',
    heading: 'Gift Guides for Toddlers',
    background: 'none',
    includeVerticalSectionSpacing: false,
  },
  {
    type: 'guides',
    id: '5',
    heading: 'Gift Guides for Toddlers',
    slugs: [
      'best-toys-for-one-year-olds',
      'best-montessori-toys-toddlers',
      'best-play-kitchens',
      'best-affordable-gifts-for-toddlers',
      'best-toddler-gifts',
      'best-stocking-stuffers-toddlers',
      'best-toys-for-two-year-olds',
      'best-musical-toys',
    ],
    includeVerticalSectionSpacing: true,
    columns: 4,
  },
  {
    type: 'text',
    id: '6',
    desktopWidth: 'limited',
    heading: 'Gift Guides for Everyone in Your Life',
    background: 'none',
    includeVerticalSectionSpacing: false,
  },
  {
    type: 'guides',
    id: '7',
    heading: 'Gift Guides for Everyone in Your Life',
    slugs: [
      'best-gifts-new-moms',
      'best-gifts-new-dads',
      'best-holiday-gifts-for-grandparents',
      'best-matching-family-holiday-pajamas',
      'quick-holiday-gifts',
      'best-unexpected-useful-gifts-parents',
      'holiday-gifts-from-black-owned-businesses',
      'gifts-that-give-back',
    ],
    includeVerticalSectionSpacing: true,
    columns: 4,
  },
  {
    type: 'feature_highlight',
    id: '8',
    imageUrl:
      'https://images.babylist.com/image/upload/b_rgb:f9f6f3,q_50/v1697832344/Dynamic%20Landing%20Pages/Holiday/favorite-gifts.jpg',
    headerText: "Babylist Shop: New Parents' Favorite Gifts",
    description: {
      content: [
        "Shop this season's best toys, books, clothes, matching family PJs and more from the most trusted & beloved brands.",
      ],
      renderAsListItems: false,
    },
    desktopWidth: 'full',
    layout: 'stacked',
    imagePlacement: 'right',
    background: 'none',
    backgroundColor: 'cream',
    callToAction: {
      type: 'link',
      id: '8-1',
      text: 'Shop Holiday Gifts',
      url: '/store/holiday-shop',
    },
    isRectangleImage: false,
  },
  {
    type: 'feature_highlight',
    id: '9',
    imageUrl:
      'https://images.babylist.com/image/upload/b_rgb:f9f6f3,q_50/v1697832339/Dynamic%20Landing%20Pages/Holiday/gift-cards.jpg',
    headerText: 'No Clue What They Want? Give a Gift Card',
    description: {
      content: [
        "Babies & toddlers can be unpredictable, but your gift doesn't have to be.",
      ],
      renderAsListItems: false,
    },
    desktopWidth: 'full',
    layout: 'stacked',
    imagePlacement: 'left',
    background: 'none',
    backgroundColor: 'cream',
    callToAction: {
      type: 'link',
      id: '9-1',
      text: 'Shop Gift Cards',
      url: '/store/gift-cards',
    },
    isRectangleImage: false,
  },
  {
    type: 'cards',
    id: '10',
    desktopWidth: 'limited',
    imageShape: 'mediumSquare',
    background: 'white',
    mobileVariant: 'stacked',
    desktopVariant: 'threeColumns',
    headerText: 'New! Holiday Wishlists',
    cards: [
      {
        type: 'card',
        id: '10-1',
        headerText: 'Lists for Every Little One',
        subHeaderContent:
          'Personalize lists with sizes, favorite colors, latest loves and things-they-have-enough-of.',
        imageUrl:
          'https://images.babylist.com/image/upload/c_crop,w_120/v1697832334/Dynamic%20Landing%20Pages/Holiday/lists.png',
      },
      {
        type: 'card',
        id: '10-2',
        headerText: 'Add Gifts from Anywhere',
        subHeaderContent:
          "We'll automagically price compare if they're sold in multiple stores.",
        imageUrl:
          'https://images.babylist.com/image/upload/c_crop,h_160,w_160/v1697832334/Dynamic%20Landing%20Pages/Holiday/add-from-anywhere.png',
      },
      {
        type: 'card',
        id: '10-3',
        headerText: 'One Easy Link to Share',
        subHeaderContent:
          'Ditch the family text-message chain of one-off gift ideas and streamline your holiday to-dos.',
        imageUrl:
          'https://images.babylist.com/image/upload/c_crop,h_160,w_160/v1697832334/Dynamic%20Landing%20Pages/Holiday/easy-link.png',
      },
    ],
    callToAction: {
      type: 'link',
      id: '10-4',
      text: 'Create a Holiday Wishlist',
      onClick: (currentUser, tracker) => {
        tracker.trackEvent({
          event: track.listTypeClicked,
          eventLocation: track.EventLocation.HOLIDAY_LANDING_PAGE,
          listType: track.ListType.HOLIDAY,
          registryOwnerId: currentUser?.id,
          registryId: currentUser?.registryId,
          isRegistryOwner:
            currentUser?.currentRegistry?.ownerId === currentUser?.id,
        })

        window.location.href = createHolidayWishlistPath
      },
    },
    isVisible: (currentUser) => !!currentUser,
  },
  {
    type: 'final_cta',
    id: '15',
    heading: 'Hand-picked ideas from our <br/> gift-obsessed editors',
    background: 'rainbow gradient',
    backgroundColor: 'white',
    textColor: 'eggplant',
    callToAction: {
      type: 'link',
      id: '11-1',
      text: 'Explore All Gift Guides',
      onClick: (currentUser, tracker) => {
        const path = '/hello-baby/holiday'

        tracker.trackEvent({
          event: track.componentClicked,
          eventType: track.EventType.CLICK,
          eventLocation: track.EventLocation.HOLIDAY_LANDING_PAGE,
          eventUrl: window.location.href,
          componentType: track.ComponentType.BOTTOM_BANNER as any,
          registryOwnerId: currentUser?.id,
          registryId: currentUser?.registryId,
          isRegistryOwner:
            currentUser?.currentRegistry?.ownerId === currentUser?.id,
          urlClicked: new URL(path, window.location.origin).toString(),
          positionInList: 1,
          slotPosition: 11,
        })

        window.location.href = path
      },
    },
    isVisible: (currentUser) => !!currentUser,
  },
  {
    type: 'final_cta',
    id: '15',
    heading: 'Hand-picked ideas from <br/> our gift-obsessed editors',
    background: 'rainbow gradient',
    backgroundColor: 'cream',
    textColor: 'eggplant',
    callToAction: {
      type: 'link',
      id: '11-1',
      text: 'Explore All Gift Guides',
      onClick: (currentUser, tracker) => {
        const path = '/hello-baby/holiday'

        tracker.trackEvent({
          event: track.componentClicked,
          eventType: track.EventType.CLICK,
          eventLocation: track.EventLocation.HOLIDAY_LANDING_PAGE,
          eventUrl: window.location.href,
          componentType: track.ComponentType.BOTTOM_BANNER as any,
          registryOwnerId: currentUser?.id,
          registryId: currentUser?.registryId,
          isRegistryOwner:
            currentUser?.currentRegistry?.ownerId === currentUser?.id,
          urlClicked: new URL(path, window.location.origin).toString(),
          positionInList: 1,
          slotPosition: 11,
        })

        window.location.href = path
      },
    },
    isVisible: (currentUser) => !currentUser,
  },
]

export default holidayPageSections
